import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Product } from '../../services/orderLinkAPI';

interface AttributeOption {
  value: string;
  label: string;
  price: number;
}

interface ProductAttributeSelectorProps {
  product: Product;
  initialValues?: Record<string, any>;
  onAttributesChange?: (attributes: Record<string, any>, finalPrice: number) => void;
  mode?: 'auto' | 'manual';
  useRadioForCheckboxes?: boolean;
  useRadioForDropdowns?: boolean;
  disabledAttributes?: string[];
  disabledAttributeOptions?: Record<string, string[]>;
  hiddenAttributes?: string[];
  ignorePriceForAttributes?: string[];
  hidePriceSummary?: boolean;
}

export interface ProductAttributeSelectorRef {
  getSummary: () => { attributes: Record<string, any>; finalPrice: number };
}

const getAttributeValues = (data: any): string[] =>
  Object.keys(data).filter(key => key !== 'default_value' && key !== 'selected');

const buildAttributeOptions = (attributeData: any): AttributeOption[] => {
  const options: AttributeOption[] = [];
  const added = new Set<string>();
  const defaultValue = attributeData.default_value;

  if (defaultValue) {
    options.push({ value: defaultValue, label: defaultValue, price: 0 });
    added.add(defaultValue);
  }

  getAttributeValues(attributeData).forEach(value => {
    if (!added.has(value)) {
      options.push({ value, label: value, price: parseFloat(attributeData[value] || '0') });
      added.add(value);
    }
  });

  return options;
};

const formatPriceDisplay = (attributeData: any, value: string, price: number): string => {
  const priceStr = attributeData[value];
  if (priceStr && typeof priceStr === 'string' && priceStr.startsWith('x')) return ` (${priceStr})`;
  if (!isNaN(price) && price !== 0) return ` (${price > 0 ? '+' : ''}R$ ${price.toFixed(2).replace('.', ',')})`;
  return '';
};

const ProductAttributeSelector = forwardRef<ProductAttributeSelectorRef, ProductAttributeSelectorProps>(({
  product,
  initialValues = {},
  onAttributesChange,
  mode = 'auto',
  useRadioForCheckboxes = false,
  useRadioForDropdowns = false,
  disabledAttributes = [],
  disabledAttributeOptions = {},
  hiddenAttributes = [],
  ignorePriceForAttributes = [],
  hidePriceSummary = false
}, ref) => {
  const getDefaultValue = (attributeName: string, attributeData: any): any => {
    if (attributeData?.default_value) return attributeData.default_value;
    if (attributeData && typeof attributeData === 'object') {
      const values = getAttributeValues(attributeData);
      return values.length > 0 ? values[0] : '';
    }
    return '';
  };

  const isSelectableAttribute = (attributeData: any): boolean => {
    if (!attributeData || typeof attributeData !== 'object') return false;
    const values = getAttributeValues(attributeData);
    const hasDefault = attributeData.hasOwnProperty('default_value');
    return (hasDefault && values.length >= 1) || values.length >= 2;
  };

  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, any>>(() => {
    const attrs: Record<string, any> = { ...initialValues };
    Object.entries(product.attributes || {}).forEach(([name, data]) => {
      if (isSelectableAttribute(data) && !attrs[name]) {
        const defaultVal = getDefaultValue(name, data);
        if (defaultVal) attrs[name] = defaultVal;
      }
    });
    return attrs;
  });

  const calculateFinalPrice = useCallback((): number => {
    let totalPrice = product.price;
    const multipliers: number[] = [];

    Object.entries(selectedAttributes).forEach(([attributeName, selectedValue]) => {
      if (ignorePriceForAttributes.includes(attributeName)) return;
      const attributeData = product.attributes[attributeName];
      if (!attributeData || typeof attributeData !== 'object') return;

      const processValue = (priceValue: string) => {
        if (priceValue.startsWith('x')) {
          const m = parseFloat(priceValue.substring(1));
          if (!isNaN(m)) multipliers.push(m);
        } else {
          const p = parseFloat(priceValue);
          if (!isNaN(p)) totalPrice += p;
        }
      };

      if (Array.isArray(selectedValue)) {
        selectedValue.forEach((v: string) => {
          if (typeof attributeData[v] === 'string') processValue(attributeData[v]);
        });
      } else if (selectedValue && typeof selectedValue === 'string') {
        if (typeof attributeData[selectedValue] === 'string') processValue(attributeData[selectedValue]);
      }
    });

    multipliers.forEach(m => { totalPrice *= m; });
    return totalPrice;
  }, [product, selectedAttributes, ignorePriceForAttributes]);

  useImperativeHandle(ref, () => ({
    getSummary: () => ({ attributes: selectedAttributes, finalPrice: calculateFinalPrice() })
  }), [selectedAttributes, calculateFinalPrice]);

  useEffect(() => {
    if (mode === 'auto' && onAttributesChange) {
      onAttributesChange(selectedAttributes, calculateFinalPrice());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, selectedAttributes]);

  const updateAttribute = (attributeName: string, value: any) => {
    setSelectedAttributes(prev => ({ ...prev, [attributeName]: value }));
  };

  const updateCheckbox = (attributeName: string, value: string, checked: boolean) => {
    if (useRadioForCheckboxes) {
      updateAttribute(attributeName, value);
    } else {
      const current = selectedAttributes[attributeName];
      const currentValues = Array.isArray(current) ? current : [];
      updateAttribute(attributeName, checked
        ? [...currentValues, value]
        : currentValues.filter((v: string) => v !== value)
      );
    }
  };

  const renderDropdownInput = (attributeName: string, attributeData: any) => {
    const allOptions = buildAttributeOptions(attributeData);

    return (
      <div key={attributeName} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">{attributeName}</label>
        <select
          value={selectedAttributes[attributeName] || getDefaultValue(attributeName, attributeData)}
          onChange={(e) => updateAttribute(attributeName, e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {allOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}{formatPriceDisplay(attributeData, option.value, option.price)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const renderRadioInput = (attributeName: string, attributeData: any) => {
    const selectedValue = selectedAttributes[attributeName] || getDefaultValue(attributeName, attributeData);
    const isDisabled = disabledAttributes.includes(attributeName);
    const allOptions = buildAttributeOptions(attributeData);
    const optionsToShow = isDisabled ? allOptions.filter(opt => opt.value === selectedValue) : allOptions;

    return (
      <div key={attributeName} className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">
          {attributeName}
          {isDisabled && (
            <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">Fixo</span>
          )}
        </label>
        <div className="grid grid-cols-1 gap-2">
          {optionsToShow.map((option) => {
            const isChecked = selectedValue === option.value;
            const isOptionDisabled = isDisabled || disabledAttributeOptions[attributeName]?.includes(option.value) || false;

            return (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-4 border-2 rounded-lg transition-all ${
                  isOptionDisabled
                    ? 'bg-gray-50 border-gray-300 cursor-not-allowed opacity-75'
                    : isChecked
                    ? 'bg-primary-50 border-primary-500 shadow-sm cursor-pointer'
                    : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm cursor-pointer'
                }`}
              >
                <input
                  type="radio"
                  name={attributeName}
                  value={option.value}
                  checked={isChecked}
                  onChange={() => updateAttribute(attributeName, option.value)}
                  disabled={isOptionDisabled}
                  className="w-5 h-5 text-primary-600 border-gray-300 focus:ring-primary-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="flex-1 text-base font-medium text-gray-900">{option.label}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const renderCheckboxInput = (attributeName: string, attributeData: any) => {
    const selectedValue = useRadioForCheckboxes
      ? selectedAttributes[attributeName]
      : (selectedAttributes[attributeName] || []);
    const allOptions = buildAttributeOptions(attributeData);

    return (
      <div key={attributeName} className="space-y-3">
        <label className="block text-sm font-medium text-gray-700">{attributeName}</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {allOptions.map((option) => {
            const isChecked = useRadioForCheckboxes
              ? selectedValue === option.value
              : Array.isArray(selectedValue) && selectedValue.includes(option.value);
            const displayPrice = formatPriceDisplay(attributeData, option.value, option.price);
            const isOptionDisabled = disabledAttributeOptions[attributeName]?.includes(option.value) || false;

            return (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                  isOptionDisabled
                    ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                    : isChecked
                    ? 'bg-blue-50 border-blue-300 text-blue-900 cursor-pointer'
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer'
                }`}
              >
                <input
                  type={useRadioForCheckboxes ? "radio" : "checkbox"}
                  name={useRadioForCheckboxes ? attributeName : undefined}
                  checked={isChecked}
                  onChange={(e) => updateCheckbox(attributeName, option.value, e.target.checked)}
                  disabled={isOptionDisabled}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
                />
                <span className="flex-1 text-sm">
                  {option.label}
                  {displayPrice && <span className="ml-2 text-xs font-medium">{displayPrice}</span>}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const selectableAttributes = Object.entries(product.attributes || {}).filter(([name, data]) =>
    isSelectableAttribute(data) && !hiddenAttributes.includes(name)
  );

  if (selectableAttributes.length === 0) {
    return (
      <div className="text-center text-gray-500 py-4">
        Este produto não possui atributos configuráveis.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {selectableAttributes.map(([attributeName, attributeData]) => {
        const totalOptions = getAttributeValues(attributeData).length + (attributeData.hasOwnProperty('default_value') ? 1 : 0);
        const useCheckbox = totalOptions > 3;

        if (useCheckbox) return renderCheckboxInput(attributeName, attributeData);
        return useRadioForDropdowns
          ? renderRadioInput(attributeName, attributeData)
          : renderDropdownInput(attributeName, attributeData);
      })}

      {!hidePriceSummary && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-blue-900">Preço final:</span>
            <span className="text-lg font-bold text-blue-900">
              R$ {calculateFinalPrice().toFixed(2).replace('.', ',')}
            </span>
          </div>
          {calculateFinalPrice() !== product.price && (
            <div className="text-xs text-blue-700 mt-1">
              Preço base: R$ {product.price.toFixed(2).replace('.', ',')} + personalizações
            </div>
          )}
        </div>
      )}
    </div>
  );
});

ProductAttributeSelector.displayName = 'ProductAttributeSelector';

export default ProductAttributeSelector;
